import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeadphones, faCheck } from '@fortawesome/free-solid-svg-icons';
import '../../styles/meetings.css';

/**
 * Where the meeting's sound comes out: the phone's speaker, its earpiece, a
 * wired headset, Bluetooth earbuds.
 *
 * Where the browser lets a page choose (Chrome and Edge, on computers and on
 * Android) this lists what is connected and switches to it. Where it does not -
 * iPhone Safari never has - the operating system alone decides, so instead of a
 * list that could not work this says how the phone does it. Either way a person
 * on a phone is never left wondering why the meeting is coming out of the
 * loudspeaker.
 */

const isDefault = (device) => !device.deviceId || device.deviceId === 'default';

const SpeakerMenu = ({ supported, outputs, current, onSelect, onPickRoute = null, routes = [], routeId = '', onRoute = () => { }, noise = null }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    // Tapping anywhere else, or Escape, closes it.
    useEffect(() => {
        if (!open) return undefined;
        const away = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        const key = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('pointerdown', away);
        document.addEventListener('keydown', key);
        return () => {
            document.removeEventListener('pointerdown', away);
            document.removeEventListener('keydown', key);
        };
    }, [open]);

    // Nothing chosen yet means the browser's default is in use.
    const active = current || (outputs.find(isDefault) || outputs[0] || {}).deviceId;

    const choose = (deviceId) => {
        onSelect(deviceId);
        setOpen(false);
    };

    return (
        <div className="mt-speaker" ref={wrapRef}>
            <button
                type="button"
                className="mt-control"
                onClick={() => setOpen((o) => !o)}
                aria-label="Sound output"
                aria-expanded={open}
                title="Where the sound plays"
            >
                <FontAwesomeIcon icon={faHeadphones} />
            </button>

            {open && (
                <div className="mt-speaker-menu" role="menu">
                    <strong>Sound plays through</strong>

                    {supported && outputs.length > 0 && (
                        <ul>
                            {outputs.map((device) => (
                                <li key={device.deviceId || device.label}>
                                    <button type="button" role="menuitemradio" aria-checked={device.deviceId === active} onClick={() => choose(device.deviceId)}>
                                        <span>{device.label}</span>
                                        {device.deviceId === active && <FontAwesomeIcon icon={faCheck} />}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {supported && outputs.length === 0 && (
                        <p>Your device only offers its default output.</p>
                    )}

                    {!onPickRoute && outputs.length <= 1 && routes.length > 1 && (
                        <ul>
                            {routes.map((route) => (
                                <li key={route.deviceId}>
                                    <button type="button" role="menuitemradio" aria-checked={route.deviceId === routeId} onClick={() => { onRoute(route.deviceId); setOpen(false); }}>
                                        <span>{route.label}</span>
                                        {route.deviceId === routeId && <FontAwesomeIcon icon={faCheck} />}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {supported && !(routes.length > 1) && outputs.length <= 1 && !onPickRoute && (
                        <p>
                            On Android the browser cannot move the sound by itself. Swipe down from the top of
                            the screen and tap the media output (or Bluetooth) button to pick the phone speaker or your earbuds.
                        </p>
                    )}

                    {onPickRoute && (
                        <button type="button" className="mt-btn ghost mt-route-btn" onClick={() => { onPickRoute(); setOpen(false); }}>
                            Choose speaker or earbuds
                        </button>
                    )}

                    {!supported && !onPickRoute && routes.length <= 1 && (
                        <p>
                            Your phone decides where the sound plays. Connect Bluetooth earbuds or a headset and it
                            switches to them by itself. To move it between the speaker and the earpiece, use your
                            phone's sound controls.
                        </p>
                    )}

                    {noise && (
                        <label className="mt-noise-toggle">
                            <input type="checkbox" checked={noise.on} onChange={noise.onToggle} />
                            <span>Noise cancellation<small>Cuts keyboard, fan and background noise from your microphone</small></span>
                        </label>
                    )}

                    <small>
                        A Bluetooth device you cannot see here has to be connected in your phone's settings first.
                    </small>
                </div>
            )}
        </div>
    );
};

export default SpeakerMenu;
